import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { Checkbox } from "@/components/podkit/checkbox/Checkbox";
import { Pill } from "@/components/podkit/status/Pill";
import { useMembers } from "@/queries/organization-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useState, useMemo, type FC } from "react";
import { UserStatus } from "gitpod-next-api/gitpod/v1/user_pb";

type MemberSelectionListProps = {
    excludeUserIds?: Set<string>;
    selectedUserIds?: Set<string>;
    onChangeSelection: (selectedUserIds: Set<string>) => void;
    showSearch?: boolean;
    showSelectedCount?: boolean;
    className?: string;
};

export const MemberSelectionList: FC<MemberSelectionListProps> = ({
    excludeUserIds,
    selectedUserIds = new Set(),
    onChangeSelection,
    showSearch = true,
    showSelectedCount = true,
    className,
}) => {
    const { data: user } = useAuthenticatedUser();
    const { data: membersData } = useMembers();
    const [searchValue, setSearchValue] = useState("");

    // Filter available org members (not excluded, and active)
    const availableMembers = useMemo(() => {
        if (!membersData?.members) return [];

        return membersData.members.filter(
            (member) => member.userId && member.status === UserStatus.ACTIVE && !excludeUserIds?.has(member.userId),
        );
    }, [membersData?.members, excludeUserIds]);

    // Filter members by search
    const filteredMembers = useMemo(() => {
        if (!searchValue.trim()) return availableMembers;

        const query = searchValue.trim().toLowerCase();
        return availableMembers.filter(
            (member) => member.fullName?.toLowerCase().includes(query) || member.email?.toLowerCase().includes(query),
        );
    }, [availableMembers, searchValue]);

    const selectedCount = selectedUserIds.size;

    return (
        <div className={className}>
            {/* Search Input */}
            {showSearch && (
                <SearchInput
                    wrapperClassName="w-full"
                    className="max-w-none"
                    placeholder="Search by name or keyword"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onClear={() => setSearchValue("")}
                    data-tracking-id="member-selection-search"
                />
            )}

            {/* Members List */}
            <ScrollArea orientation="vertical" className="min-h-0 flex-1 rounded-lg border border-border-base">
                {filteredMembers.length === 0 ? (
                    <div className="p-4 text-center text-content-secondary">
                        {searchValue.trim() ? "No members found matching your search." : "No available members to add."}
                    </div>
                ) : (
                    <div className="divide-y divide-border-base">
                        {filteredMembers.map((member) => {
                            const isMemberCurrentUser = member.userId === user?.id;
                            const loginProvider = (() => {
                                switch (member.loginProvider) {
                                    case "magiclink":
                                        return "Email";
                                    case "github":
                                        return "GitHub";
                                    case "gitlab":
                                        return "GitLab";
                                    case "google":
                                        return "Google";
                                    case "custom":
                                        return "SSO";
                                    default:
                                        return "";
                                }
                            })();
                            const isSelected = selectedUserIds.has(member.userId);
                            return (
                                <label
                                    key={member.userId}
                                    htmlFor={`member-checkbox-${member.userId}`}
                                    className="ml-2 flex cursor-pointer items-center gap-2 px-2 py-2 hover:bg-surface-secondary"
                                    data-tracking-id="member-selection-row"
                                >
                                    <Checkbox
                                        id={`member-checkbox-${member.userId}`}
                                        checked={isSelected}
                                        onCheckedChange={(checked) => {
                                            const newSet = new Set(selectedUserIds);
                                            if (checked) {
                                                newSet.add(member.userId);
                                            } else {
                                                newSet.delete(member.userId);
                                            }
                                            onChangeSelection(newSet);
                                        }}
                                        data-testid={`member-checkbox-${member.userId}`}
                                        data-tracking-id="member-selection-checkbox"
                                    />
                                    <Avatar
                                        size={24}
                                        className="size-6 flex-shrink-0 rounded-full border-2 border-surface-glass bg-surface-glass ring-1 ring-border-light"
                                    >
                                        {member.avatarUrl && (
                                            <AvatarImage src={member.avatarUrl} alt={`${member.fullName}'s avatar`} />
                                        )}
                                        <AvatarFallback>
                                            <AvatarInitials name={member.fullName} size={24} />
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex min-w-0 flex-1 items-center gap-2">
                                        <p className="truncate font-medium text-content-primary">{member.fullName}</p>
                                        {isMemberCurrentUser && <Pill variant="success">me</Pill>}
                                        {loginProvider && (
                                            <span className="text-sm text-content-secondary">{loginProvider}</span>
                                        )}
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                )}
            </ScrollArea>

            {showSelectedCount && (
                <p className="flex-shrink-0 text-sm text-content-secondary" style={{ minHeight: "1.25rem" }}>
                    {selectedCount > 0 && `${selectedCount} people selected`}
                </p>
            )}
        </div>
    );
};
